/**
 * Utility for handling API retries with exponential backoff
 */

export interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  shouldRetry?: (error: Error | unknown) => boolean;
}

export const defaultRetryConfig: Required<RetryConfig> = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  shouldRetry: (error: Error | unknown) => {
    // Don't retry on 404, 401, 403, 408, or 422 errors
    const status = (error as any).response?.status;
    return !status || status >= 500 || status === 429 || status === 408;
  },
};

// Special retry config for rate-limited endpoints
export const rateLimitRetryConfig: Required<RetryConfig> = {
  maxRetries: 2, // Fewer retries for rate limits
  baseDelay: 2000, // Start with 2 seconds
  maxDelay: 60000, // Max 60 seconds for rate limits
  shouldRetry: (error: Error | unknown) => {
    const status = (error as any).response?.status;
    // Only retry on 429 (rate limit) and 5xx errors
    return status === 429 || status >= 500;
  },
};

// Simple circuit breaker for rate-limited endpoints
class RateLimitCircuitBreaker {
  private readonly failures: Map<string, { count: number; lastFailure: number }> = new Map();
  private readonly maxFailures = 3;
  private readonly resetTimeout = 60000; // 1 minute

  isOpen(endpoint: string): boolean {
    const failure = this.failures.get(endpoint);
    if (!failure) return false;

    const now = Date.now();
    if (now - failure.lastFailure > this.resetTimeout) {
      this.failures.delete(endpoint);
      return false;
    }

    return failure.count >= this.maxFailures;
  }

  recordFailure(endpoint: string): void {
    const now = Date.now();
    const failure = this.failures.get(endpoint) || { count: 0, lastFailure: 0 };

    // Reset count if enough time has passed
    if (now - failure.lastFailure > this.resetTimeout) {
      failure.count = 0;
    }

    failure.count++;
    failure.lastFailure = now;
    this.failures.set(endpoint, failure);
  }

  recordSuccess(endpoint: string): void {
    this.failures.delete(endpoint);
  }
}

export const rateLimitCircuitBreaker = new RateLimitCircuitBreaker();

/**
 * Calculate exponential backoff delay with jitter
 */
export const calculateBackoffDelay = (
  attempt: number,
  config: RetryConfig = {},
  error?: unknown,
): number => {
  const { baseDelay = 1000, maxDelay = 30000 } = { ...defaultRetryConfig, ...config };

  // For rate limit errors (429), check for Retry-After header first
  const isRateLimit = (error as any)?.response?.status === 429;
  if (isRateLimit) {
    const retryAfter = (error as any).response?.headers?.['retry-after'];
    if (retryAfter) {
      const retryAfterMs = Number.parseInt(retryAfter, 10) * 1000;
      if (!Number.isNaN(retryAfterMs)) {
        // Use the server-provided retry-after time plus some jitter
        const random = crypto.getRandomValues(new Uint32Array(1))[0] / 0xffffffff;
        return Math.min(retryAfterMs + random * 2000, maxDelay);
      }
    }
  }

  // For rate limit errors without Retry-After, use longer delays
  const effectiveBaseDelay = isRateLimit ? Math.max(baseDelay, 2000) : baseDelay;

  const delay = Math.min(effectiveBaseDelay * Math.pow(2, attempt), maxDelay);
  const random = crypto.getRandomValues(new Uint32Array(1))[0] / 0xffffffff;
  const jitter = isRateLimit ? random * 2000 : random * 1000; // More jitter for rate limits

  return delay + jitter;
};

/**
 * Retry a function with exponential backoff
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  config: RetryConfig = {},
  onRetry?: (attempt: number, delay: number, error: Error | unknown) => void,
): Promise<T> => {
  const { maxRetries = 3, shouldRetry } = { ...defaultRetryConfig, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry this error
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Calculate delay for next retry, passing error for rate limit handling
      const delay = calculateBackoffDelay(attempt, config, error);

      if (onRetry) {
        onRetry(attempt + 1, delay, error);
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

/**
 * Create a retry wrapper for API calls
 */
export const createRetryWrapper = (config: RetryConfig = {}) => {
  return <T>(
    fn: () => Promise<T>,
    onRetry?: (attempt: number, delay: number, error: Error | unknown) => void,
  ) => {
    return withRetry(fn, config, onRetry);
  };
};
